const buildPrintFrame = () => {
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.style.visibility = "hidden";
  frame.setAttribute("aria-hidden", "true");
  document.body.appendChild(frame);
  return frame;
};

const printHtml = (html) => {
  const frame = buildPrintFrame();
  const frameDoc = frame.contentDocument || frame.contentWindow?.document;

  if (!frameDoc) {
    console.error("Unable to create print document.");
    frame.remove();
    return;
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const cleanup = () => {
    setTimeout(() => frame.remove(), 250);
  };

  const triggerPrint = () => {
    const win = frame.contentWindow;
    if (!win) {
      cleanup();
      return;
    }

    try {
      win.focus();
      win.print();
    } catch (error) {
      console.error("Failed to print purchase payment receipt:", error);
    } finally {
      cleanup();
    }
  };

  frame.onload = () => {
    setTimeout(triggerPrint, 300);
  };

  if (frameDoc.fonts?.ready) {
    frameDoc.fonts.ready
      .then(() => setTimeout(triggerPrint, 300))
      .catch(() => setTimeout(triggerPrint, 300));
    return;
  }

  setTimeout(triggerPrint, 400);
};

const thermalStyles = `
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html, body {
      width: 80mm;
      margin: 0;
      padding: 0;
      background: #fff;
      color: #000;
      font-family: "Courier New", monospace;
      font-size: 12px;
      line-height: 1.2;
    }

    body {
      overflow: hidden;
    }

    .receipt {
      width: 80mm;
      padding: 6px 8px;
      text-align: center;
    }

    .company {
      font-size: 14px;
      font-weight: 700;
      margin-bottom: 2px;
    }

    .contact {
      font-size: 10px;
      margin-bottom: 4px;
    }

    .title {
      font-size: 12px;
      font-weight: 700;
      margin: 6px 0 8px;
    }

    .dash {
      border-top: 1px dashed #000;
      margin: 4px 0;
    }

    .line {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      margin: 2px 0;
      font-size: 11px;
    }

    .label {
      white-space: nowrap;
    }

    .value {
      text-align: right;
      flex: 1;
      word-break: break-word;
    }

    .center {
      text-align: center;
    }

    .bold {
      font-weight: 700;
    }

    .box {
      margin-top: 6px;
      padding-top: 4px;
    }

    @page {
      size: 80mm auto;
      margin: 2mm;
    }

    @media print {
      html, body {
        width: 80mm;
        margin: 0;
        padding: 0;
      }

      .receipt {
        width: 80mm;
        padding: 4px 6px;
      }
    }
  </style>
`;

export const printPurchasePaymentThermal = (paymentData) => {
  const currencySymbol = paymentData?.currencySymbol || "AF";
  const receiptNumber =
    paymentData?.receiptNumber || Math.floor(Math.random() * 9999);
  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Purchase Payment Receipt</title>
      ${thermalStyles}
    </head>
    <body>
      <div class="receipt">
        <div class="company">Habib Gilaman</div>
        <div class="contact">0767080919 - 0767080919</div>

        <div class="title">PURCHASE PAYMENT RECEIPT</div>
        <div class="dash"></div>

        <div class="line">
          <span class="label">Receipt:</span>
          <span class="value">${receiptNumber}</span>
        </div>
        <div class="line">
          <span class="label">Date:</span>
          <span class="value">${paymentData?.date || new Date().toLocaleDateString()}</span>
        </div>
        <div class="line">
          <span class="label">Time:</span>
          <span class="value">${new Date().toLocaleTimeString()}</span>
        </div>

        <div class="dash"></div>

        <div class="line">
          <span class="label">Supplier:</span>
          <span class="value">${paymentData?.supplierName || "Cash"}</span>
        </div>
        <div class="line">
          <span class="label">Purchase:</span>
          <span class="value">#${paymentData?.purchaseId || "N/A"}</span>
        </div>
        <div class="line">
          <span class="label">Paid:</span>
          <span class="value">${paymentData?.requestedAmount || "0.00"} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span class="label">Currency:</span>
          <span class="value">${currencySymbol}</span>
        </div>

        ${
          paymentData?.remainingAmount !== undefined &&
          paymentData?.remainingAmount !== null &&
          paymentData?.remainingAmount !== ""
            ? `
        <div class="box">
          <div class="dash"></div>
          <div class="line bold">
            <span class="label">Remaining:</span>
            <span class="value">${paymentData.remainingAmount} ${currencySymbol}</span>
          </div>
        </div>
        `
            : ""
        }

        ${
          paymentData?.notes
            ? `
        <div class="dash"></div>
        <div class="line">
          <span class="label">Notes:</span>
          <span class="value">${paymentData.notes}</span>
        </div>
        `
            : ""
        }

        <div class="dash"></div>

        <div class="center" style="margin-top: 10px; font-size: 10px;">
          Thank you!<br>
          ${new Date().getFullYear()} &copy; Habib Gilaman<br>
          <span style="font-size:9px;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</span>
        </div>
      </div>
    </body>
    </html>
  `;

  printHtml(html);
};

export const printPurchasePaymentThermalData = (purchase, paymentAmount) => {
  const formatCurrency = (currencyObj) => {
    if (!currencyObj) return "0.00";
    if (typeof currencyObj === "object") {
      return String(Object.values(currencyObj)[0] || "0.00");
    }
    return String(currencyObj);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Purchase Payment Receipt</title>
      ${thermalStyles}
    </head>
    <body>
      <div class="receipt">
        <div class="company">Habib Gilaman</div>
        <div class="contact">0767080919 - 0767080919</div>

        <div class="title">PURCHASE PAYMENT RECEIPT</div>
        <div class="dash"></div>

        <div class="line">
          <span class="label">Purchase:</span>
          <span class="value">#${purchase.id}</span>
        </div>
        <div class="line">
          <span class="label">Date:</span>
          <span class="value">${formatDate(purchase.purchase_date)}</span>
        </div>
        <div class="line">
          <span class="label">Time:</span>
          <span class="value">${new Date().toLocaleTimeString()}</span>
        </div>

        <div class="dash"></div>

        <div class="line">
          <span class="label">Supplier:</span>
          <span class="value">${purchase.supplier_details?.name || "Cash"}</span>
        </div>
        <div class="line">
          <span class="label">Total Amount:</span>
          <span class="value">${formatCurrency(purchase.total_price)} AF</span>
        </div>
        <div class="line">
          <span class="label">Amount Paid:</span>
          <span class="value">${formatCurrency(purchase.total_paid)} AF</span>
        </div>
        <div class="line">
          <span class="label">Payment Now:</span>
          <span class="value">${paymentAmount || "0.00"} AF</span>
        </div>

        <div class="dash"></div>

        <div class="center" style="margin-top: 10px; font-size: 10px;">
          Thank you!<br>
          ${new Date().getFullYear()} &copy; Habib Gilaman<br>
          <span style="font-size:9px;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</span>
        </div>
      </div>
    </body>
    </html>
  `;

  printHtml(html);
};
